import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Orbit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BrandButton } from "@/components/common/BrandButton";
import { trackEvent } from "@/services/analytics/analyticsService";
import "@/components/landing/landing-hero.css";

const secondaryBtnClass =
  "border border-zinc-700 text-white hover:text-white rounded-full px-6 py-3 hover:bg-zinc-900/50 bg-zinc-900/80 h-auto text-sm";

const HERO_VIDEO_WEBM = "/videos/fivi360-hero.webm";
const HERO_VIDEO_MP4 = "/videos/fivi360-hero.mp4";

function useDecorativeHeroVideo() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const sync = () => {
      setEnabled(!motionQuery.matches);
    };

    sync();
    motionQuery.addEventListener("change", sync);

    return () => {
      motionQuery.removeEventListener("change", sync);
    };
  }, []);

  return enabled;
}

export function LandingHero() {
  const showVideo = useDecorativeHeroVideo();

  return (
    <section
      className="relative overflow-hidden bg-[#050505] pt-16 md:pt-24 lg:pt-32 pb-16 md:pb-24 lg:pb-28 min-h-[90vh] flex items-end md:items-center"
      data-testid="landing-hero"
    >
      {showVideo ? (
        <>
          <video
            className="landing-hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
            data-testid="landing-hero-video"
          >
            <source src={HERO_VIDEO_WEBM} type="video/webm" />
            <source src={HERO_VIDEO_MP4} type="video/mp4" />
          </video>
          <div className="landing-hero-overlay" aria-hidden="true" />
        </>
      ) : null}
      <div className="relative z-[1] w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-1 gap-8 lg:gap-12 items-center">
          <div className="text-left gap-4 flex flex-col items-start">
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-white mb-2 md:mb-6"
              data-testid="landing-hero-title"
            >
              Seu <b>projeto</b> merece mais do que imagens. Merece uma <b>experiência</b>.
              <p> </p>
            </h1>

            <p className="text-lg md:text-xl text-[#b8b8c0] max-w-3xl mb-4 md:mb-8 leading-relaxed">
              {/* Transforme apresentações em experiências imersivas. Organize imagens por projetos, adicione
              informações interativas, compartilhe com clientes por link e publique seu portfólio online. */}
              {/* Transforme seus projetos em experiências 360° interativas que seus clientes podem explorar. Apresente cada detalhe, conecte ambientes e compartilhe tudo em um único link. */}
              Permita que seus clientes explorem cada ambiente, descubram detalhes e entendam melhor o seu projeto: tudo em uma experiência interativa.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-start w-full">
              <BrandButton asChild>
                <Link
                  to="/register"
                  data-testid="landing-hero-register-btn"
                  onClick={() => trackEvent("click_cta_start", { cta_location: "hero" })}
                >
                  Criar meu primeiro projeto
                </Link>
              </BrandButton>
              <Button variant="outline" className={secondaryBtnClass} asChild>
                <a href="#demo" data-testid="landing-hero-demo-btn">
                  Ver demonstração
                </a>
              </Button>
            </div>
          </div>

          {/* <div className="relative flex justify-center lg:justify-end">
            <div
              className="relative w-full max-w-lg aspect-[4/3] rounded-2xl border border-zinc-800 bg-zinc-900/50 overflow-hidden card-hover"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-zinc-800/40 via-transparent to-zinc-900/60" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8">
                <div className="rounded-full border border-zinc-700 bg-zinc-900/80 p-6">
                  <Orbit className="h-12 w-12 text-zinc-400" strokeWidth={1.25} />
                </div>
                <p className="text-sm text-zinc-500 text-center">
                  Experiência 360° interativa
                </p>
              </div>
              <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-zinc-600 to-transparent" />
            </div>
          </div> */}
        </div>
      </div>
    </section>
  );
}
